import type { ReactNode } from 'react';

/** Small helpers the event pages share: money, time in the venue's zone, labels. */

export const INPUT = 'rounded border border-line bg-surface px-2 py-1.5 text-sm';

/** Money as US dollars, grouped: $1,048.00 rather than $1048.00 (D-83). */
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export const dollars = (cents: number | null | undefined): string => (cents === null || cents === undefined ? '—' : USD.format(cents / 100));

/** "45.00" or "45" → 4500; blank → null. */
export const toCents = (value: string): number | null => {
  const trimmed = value.trim().replace(/[$,]/g, '');
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

/**
 * Ledger codes that still carry BRE's name (D-369). The codes stay — they are in
 * every journal line — and only what staff read changes.
 */
const CODE_WORDING: Record<string, string> = { BRE: 'house', BRE_PROMOTED: 'team promoted', RETAINED_BY_BRE: 'retained by the house' };

export const humanize = (code: string | null | undefined): string => (code ? (CODE_WORDING[code] ?? code.replaceAll('_', ' ').toLowerCase()) : '—');

/** Formats an instant as wall-clock time in a timezone: "Fri Oct 2, 7:30 PM". */
export function inZone(instant: string | null | undefined, timeZone: string): string {
  if (!instant) return '—';
  return new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(instant));
}

/** The offset (minutes) a timezone has from UTC at an instant. */
function offsetMinutes(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return (asUtc - date.getTime()) / 60000;
}

/**
 * A wall-clock time typed by the operator ("2026-10-02T19:30", from a
 * datetime-local input) in the venue's timezone, as the stored UTC timestamp.
 * The browser may be anywhere; the venue is where the show is.
 */
export function zonedToUtc(wallClock: string, timeZone: string): string | null {
  if (!wallClock) return null;
  const [datePart, timePart] = wallClock.split('T');
  if (!datePart || !timePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const guess = new Date(Date.UTC(y!, m! - 1, d!, hh!, mm ?? 0, 0));
  // Two passes handle a DST boundary between the guess and the answer.
  let utc = guess.getTime() - offsetMinutes(guess, timeZone) * 60000;
  utc = guess.getTime() - offsetMinutes(new Date(utc), timeZone) * 60000;
  return new Date(utc).toISOString();
}

/** The reverse: a stored instant as the datetime-local value in the venue's timezone. */
export function utcToZoned(instant: string | null | undefined, timeZone: string): string {
  if (!instant) return '';
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(instant));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

export function Tabs<T extends string>({ tabs, active, onChange }: { tabs: { id: T; label: string; badge?: ReactNode }[]; active: T; onChange: (id: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-line" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          onClick={() => onChange(tab.id)}
          className={`-mb-px rounded-t border-b-2 px-3 py-2 text-sm ${active === tab.id ? 'border-accent font-medium text-ink-900' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
        >
          {tab.label}
          {tab.badge !== undefined ? <span className="tabular ml-1.5 text-xs text-ink-400">{tab.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}
